import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UserAvatar from "@/components/app/UserAvatar";
import { Trophy, Zap, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Visible to GM / Super Admin / Property Admin only — surfaces team members
 * who've been proactive about accepting department-assigned tickets and
 * offers a one-click "Send appreciation" button.
 */
export default function ProactivityRecommendations() {
  const { user } = useAuth();
  const canSee = ["super_admin", "general_manager", "property_admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!canSee) return;
    api.get("/conduct/recommendations").then(({ data }) => setData(data)).catch(() => setData({ recommended: [], weekly_top: [] }));
  }, [canSee]);

  if (!canSee || !data) return null;
  if (data.recommended.length === 0 && data.weekly_top.length === 0) return null;

  const sendAppreciation = async (rec) => {
    setBusyId(rec.user?.id);
    try {
      await api.post("/appreciations", { user_id: rec.user.id, reason: rec.reason_suggestion });
      toast.success(`Appreciation sent to ${rec.user.name}`);
      // remove from local list
      setData((d) => ({ ...d, recommended: d.recommended.filter((r) => r.user?.id !== rec.user?.id) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send appreciation");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid="proactivity-recommendations">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-amber-500" />
        <h3 className="font-display text-base sm:text-lg font-bold tracking-tight">Proactive responders</h3>
        <span className="text-[10px] uppercase tracking-widest text-zinc-400">based on ticket acceptance speed</span>
      </div>

      {data.recommended.length > 0 && (
        <>
          <div className="label-overline mb-2">Recommended for appreciation</div>
          <ul className="space-y-2 mb-4">
            {data.recommended.map((rec) => (
              <li key={rec.user?.id} className="flex items-center gap-3 p-2 rounded-md bg-emerald-50/40 border border-emerald-100" data-testid={`recommend-${rec.user?.id}`}>
                <UserAvatar user={rec.user} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{rec.user?.name}</div>
                  <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3">
                    <span><Zap className="w-3 h-3 inline" /> median {Math.round(rec.median_response_seconds / 60)} min</span>
                    <span><strong>{rec.accepted_count}</strong> tickets accepted (30d)</span>
                  </div>
                </div>
                <button
                  onClick={() => sendAppreciation(rec)}
                  disabled={busyId === rec.user?.id}
                  className="text-xs font-semibold inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded disabled:opacity-50"
                  data-testid={`recommend-appreciate-${rec.user?.id}`}
                >
                  {busyId === rec.user?.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Send appreciation
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {data.weekly_top.length > 0 && (
        <>
          <div className="label-overline mb-2">Fastest responders this week</div>
          <ol className="space-y-1">
            {data.weekly_top.map((r, i) => (
              <li key={r.user?.id} className="flex items-center gap-3 text-sm">
                <span className="w-5 text-center font-bold text-zinc-400">{i + 1}</span>
                <UserAvatar user={r.user} size={24} />
                <span className="flex-1 truncate">{r.user?.name}</span>
                <span className="text-[11px] text-zinc-500 tabular-nums">{Math.round(r.median_response_seconds / 60)} min · {r.accepted_count}</span>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}
